import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import { createPlaceholderDocxFile } from '../utils/createPlaceholderDocxFile'
import { getContractTemplateApiErrorMessage } from './contractTemplate.api.utils'
import type {
  ContractTemplate,
  ContractTemplateDeleteResponse,
} from './contractTemplate.types'
import type {
  ContractConstructorData,
  ContractConstructorParams,
  CreateContractFromTemplatePayload,
  CreatedUserContract,
} from './contractTemplateConstructor.types'

type ContractTemplatePayload = {
  name: string
  file?: File
}

const appendTemplateFormData = (
  formData: FormData,
  { name, file }: ContractTemplatePayload,
) => {
  formData.append('name', name.trim())
  if (file) {
    formData.append('file', file)
  }
}

const postTemplateForm = async (
  url: string,
  payload: ContractTemplatePayload,
): Promise<ContractTemplate> => {
  const formData = new FormData()
  appendTemplateFormData(formData, payload)

  const res = await API.post<ResponseWrapper<ContractTemplate>>(url, formData)

  if (isError(res)) {
    throw new Error(
      getContractTemplateApiErrorMessage(res, 'Не удалось сохранить шаблон'),
    )
  }

  if (!res.data?.data) {
    throw new Error('Не удалось сохранить шаблон')
  }

  return res.data.data
}

export const contractTemplateApi = {
  async getList(search?: string): Promise<ContractTemplate[]> {
    const params = search?.trim() ? { search: search.trim() } : {}
    const res = await API.get<ResponseWrapper<ContractTemplate[]>>(
      `contract-template/index${getParams(params)}`,
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(
          res,
          'Не удалось загрузить список шаблонов',
        ),
      )
    }

    return res.data?.data ?? []
  },

  async create(name: string): Promise<ContractTemplate> {
    const normalizedName = name.trim()

    if (!normalizedName) {
      throw new Error('Введите название документа')
    }

    return postTemplateForm('contract-template/create', {
      name: normalizedName,
      file: createPlaceholderDocxFile(),
    })
  },

  async createWithFile(name: string, file: File): Promise<ContractTemplate> {
    const normalizedName = name.trim()

    if (!normalizedName) {
      throw new Error('Введите название договора')
    }

    return postTemplateForm('contract-template/create', {
      name: normalizedName,
      file,
    })
  },

  async update(
    id: number,
    payload: ContractTemplatePayload,
  ): Promise<ContractTemplate> {
    const normalizedName = payload.name.trim()

    if (!normalizedName) {
      throw new Error('Введите название договора')
    }

    return postTemplateForm(
      `contract-template/update${getParams({ id })}`,
      {
        name: normalizedName,
        file: payload.file,
      },
    )
  },

  async delete(id: number): Promise<boolean> {
    const res = await API.delete<
      ResponseWrapper<ContractTemplateDeleteResponse>
    >(`contract-template/delete${getParams({ id })}`)

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(
          res,
          'Не удалось удалить шаблон',
        ),
      )
    }

    return Boolean(res.data?.data?.deleted)
  },

  async getConstructor(
    params: ContractConstructorParams,
  ): Promise<ContractConstructorData> {
    const res = await API.get<ResponseWrapper<ContractConstructorData>>(
      `contract-template/constructor${getParams(params)}`,
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(
          res,
          'Не удалось загрузить конструктор договора',
        ),
      )
    }

    if (!res.data?.data) {
      throw new Error('Не удалось загрузить конструктор договора')
    }

    return res.data.data
  },

  async createContractFromTemplate(
    payload: CreateContractFromTemplatePayload,
  ): Promise<CreatedUserContract> {
    const res = await API.post<ResponseWrapper<CreatedUserContract>>(
      'user/create-contract-from-template',
      payload,
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(
          res,
          'Не удалось создать договор из шаблона',
        ),
      )
    }

    if (!res.data?.data) {
      throw new Error('Не удалось создать договор из шаблона')
    }

    return res.data.data
  },
}
